import type { ReactNode } from 'react';
import { Badge } from '@/components/data-display/Badge';
import { label, type ScreeningDetail } from './types';
import { CopyButton, Facts, Structured, time } from './shared';

type Result = ScreeningDetail['result'];
type AmlValue = Result['counterparty_aml'] | Result['standalone_aml'];

const EVIDENCE_LABELS = {
  completed_clear: 'Completed, no findings',
  completed_findings: 'Completed, findings returned',
  incomplete: 'Incomplete evidence',
  invalid: 'Invalid or contradictory evidence',
} as const;
const EVIDENCE_EXPLANATIONS: Record<string, string> = {
  incomplete:
    'The CBS could not confirm this check completed for the requested party. It is not a clean result.',
  invalid:
    'The response contradicts the request or itself. It is not a clean result and needs review.',
};
const CANDIDATE_NOTE =
  'Candidate findings are possible matches returned by the provider. They are not confirmed identity matches.';

function Heading({ children }: { children: ReactNode }) {
  return <h3 className="mb-3 text-sm font-semibold text-[#333]">{children}</h3>;
}
function Note({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'warn' }) {
  return (
    <p
      className={`rounded-md p-3 text-sm ${
        tone === 'warn' ? 'bg-[#fff8ee] text-[#784600]' : 'bg-[#fafafa] text-[#666]'
      }`}
    >
      {children}
    </p>
  );
}

/** One-line AML state. Missing or unvalidated evidence is never summarised as clear. */
export function amlSummary(value: AmlValue, unavailable = false): string {
  if (!value) return unavailable ? 'Unavailable — not a clear result' : 'Not returned';
  const state = value.evidence?.state;
  if (!state) return `Reported ${label(value.decision).toLowerCase()}; evidence not validated`;
  if (state === 'completed_findings')
    return `${value.hit_count} candidate finding${value.hit_count === 1 ? '' : 's'} returned`;
  return EVIDENCE_LABELS[state];
}

export function Overview({ row }: { row: ScreeningDetail }) {
  const result = row.result;
  const matched = result.rule_runs.filter((rule) => rule.matched);
  const unavailable = result.transaction_evidence?.counterparty_aml.availability === 'unavailable';
  return (
    <>
      <div>
        <Heading>Provider assessment</Heading>
        <Facts
          values={{
            'Risk score': result.score,
            Severity: label(result.severity),
            'Provider status': result.raw_status,
            'Provider reason': result.reason,
          }}
        />
      </div>
      <div>
        <Heading>Findings at a glance</Heading>
        <Facts
          values={{
            Rules: result.rule_runs.length
              ? `${matched.length} of ${result.rule_runs.length} matched`
              : 'Not returned',
            'Counterparty AML': amlSummary(result.counterparty_aml, unavailable),
            'Standalone AML': amlSummary(result.standalone_aml),
          }}
        />
      </div>
      <Note>
        Zero hits or an APPROVED provider status does not prove all required AML checks completed,
        including native fiat counterparty AML. The authoritative provider completion contract
        remains unresolved.
      </Note>
    </>
  );
}

export function Rules({ rules }: { rules: Result['rule_runs'] }) {
  if (rules.length === 0) return <p className="text-sm text-[#777]">Not returned.</p>;
  const ordered = [...rules].sort((a, b) => Number(b.matched) - Number(a.matched));
  return (
    <ul className="divide-y divide-[#eee] rounded-md border border-[#eee]">
      {ordered.map((rule, i) => (
        <li
          key={`${rule.rule_id}-${i}`}
          className={`flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between ${
            rule.matched ? 'bg-[#fffaf3]' : ''
          }`}
        >
          <div className="min-w-0">
            <p className="break-words text-sm font-semibold text-[#333]">{rule.title}</p>
            <p className="mt-1 text-xs text-[#777]">
              Mode <span className="rounded bg-[#f5f5f5] px-1.5 py-0.5">{rule.mode}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge variant={rule.matched ? 'warning' : 'default'}>
              {rule.matched ? 'Matched' : 'Unmatched'}
            </Badge>
            <span className="text-[#555]">Score delta {rule.score_delta ?? 'not returned'}</span>
            {rule.severity && <span className="text-[#555]">Severity {label(rule.severity)}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function Aml({ value }: { value: AmlValue }) {
  if (!value) return <p className="text-sm text-[#777]">Not returned.</p>;
  const evidence = value.evidence;
  return (
    <div className="space-y-3">
      <Facts
        values={{
          'Evidence state': evidence
            ? EVIDENCE_LABELS[evidence.state]
            : 'Not validated (stored before evidence checks)',
          'Provider status': value.raw_status,
          'Reported decision': label(value.decision),
          'Candidate findings': value.hit_count,
          ...('score' in value ? { Score: value.score } : {}),
          'Identity check':
            value.identity &&
            [
              label(value.identity.status),
              value.identity.mismatched.length
                ? `mismatched: ${value.identity.mismatched.join(', ')}`
                : null,
              value.identity.unverified.length
                ? `not echoed: ${value.identity.unverified.join(', ')}`
                : null,
            ]
              .filter(Boolean)
              .join(' — '),
          'Screened name (as returned)': value.screened?.full_name,
          'Screened type (as returned)': value.screened?.entity_type,
        }}
      />
      {evidence && EVIDENCE_EXPLANATIONS[evidence.state] && (
        <Note tone="warn">
          {EVIDENCE_EXPLANATIONS[evidence.state]}
          {evidence.issues.length ? ` Reasons: ${evidence.issues.join(', ')}.` : ''}
        </Note>
      )}
      {value.warnings?.length ? (
        <ul className="space-y-2 text-sm">
          {value.warnings.map((warning, i) => (
            <li key={i} className="rounded-md bg-[#fafafa] p-3">
              <span className="font-medium">{warning.risk ?? warning.feature ?? 'Warning'}</span>
              {warning.log_type ? ` (${warning.log_type})` : ''}
              {warning.short_description ? ` — ${warning.short_description}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {value.hits?.length ? (
        <>
          <Note tone="warn">{CANDIDATE_NOTE}</Note>
          <ul className="space-y-3">
            {value.hits.map((hit, i) => (
              <li key={i} className="rounded-md border border-[#eee] p-3">
                <p className="mb-2 text-sm font-semibold text-[#333]">
                  Candidate: {hit.name ?? 'Name not returned'}
                </p>
                <Facts
                  values={{
                    'Match score': hit.match_score,
                    Categories: hit.kinds.map(label).join(', '),
                    Datasets: hit.datasets.join(', '),
                    'Source URL (text)': hit.url,
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-[#777]">No individual findings returned.</p>
      )}
    </div>
  );
}

export function CounterpartyAml({ result }: { result: Result }) {
  const evidence = result.transaction_evidence;
  return (
    <>
      {!evidence ? (
        <Note>Attribution evidence not recorded (stored before evidence checks).</Note>
      ) : (
        <Note tone="warn">
          {evidence.counterparty_aml.availability === 'unavailable'
            ? 'The provider returned no counterparty AML result at any location the CBS can attribute to the counterparty. Counterparty AML is unavailable, not clear.'
            : 'A counterparty AML block was read from a location the current provider contract does not tie to a party. It is shown for review and is never treated as a completed check.'}
        </Note>
      )}
      <Aml value={result.counterparty_aml} />
    </>
  );
}

export function StandaloneAml({ result }: { result: Result }) {
  const late = result.provider_after_manual;
  return (
    <>
      <p className="text-sm text-[#666]">
        A separate person AML check on an incoming sender whose entity type is unknown. A hit or
        review hold here keeps the transaction on hold whatever the transaction decision.
      </p>
      <Aml value={result.standalone_aml} />
      <div>
        <Heading>Provider evidence after manual decision</Heading>
        {late ? (
          <div className="space-y-3">
            <Facts
              values={{
                Status: late.raw_status,
                Decision: label(late.decision),
                Received: time(late.received_at),
              }}
            />
            <Note>This evidence does not replace the manual screening decision.</Note>
            <Aml value={late.standalone_aml ?? null} />
          </div>
        ) : (
          <p className="text-sm text-[#777]">Not returned.</p>
        )}
      </div>
    </>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  manual: 'CBS manual decision',
  didit_override: 'Didit console reviewer',
};

export function ReviewHistory({ row }: { row: ScreeningDetail }) {
  const activities = row.result.provider_activities ?? [];
  const events = [...row.events].sort(
    (a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at),
  );
  return (
    <>
      <div>
        <Heading>Reviewer decision</Heading>
        {row.override.status === 'none' ? (
          <p className="text-sm text-[#777]">No reviewer decision recorded.</p>
        ) : (
          <div className="rounded-md border border-[#eee] p-4">
            <Facts
              values={{
                Decision: label(row.override.status),
                Reviewer: row.override.by,
                Reason: row.override.reason,
                Time: time(row.override.at),
                Source: row.decision_source
                  ? (SOURCE_LABELS[row.decision_source] ?? label(row.decision_source))
                  : null,
              }}
            />
          </div>
        )}
      </div>
      <div>
        <Heading>Provider review activity</Heading>
        {activities.length === 0 ? (
          <p className="text-sm text-[#777]">No provider reviewer activity returned.</p>
        ) : (
          <ol className="space-y-2">
            {activities.map((activity) => (
              <li key={activity.id} className="rounded-md bg-[#fafafa] p-3 text-sm">
                <p className="font-medium text-[#333]">
                  {label(activity.previousStatus)} → {label(activity.newStatus)}
                </p>
                <p className="mt-1 text-[#666]">
                  {activity.actorName ?? activity.actorEmail ?? 'Reviewer not returned'}
                  {activity.actorName && activity.actorEmail
                    ? ` (${activity.actorEmail})`
                    : ''} · {time(activity.occurredAt)}
                </p>
                {(activity.reasonLabel ?? activity.reasonCode) && (
                  <p className="mt-1 text-[#666]">
                    Reason: {activity.reasonLabel ?? activity.reasonCode}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
      <div>
        <Heading>Screening timeline</Heading>
        <p className="mb-3 text-xs text-[#777]">
          Delivery to the clients webapp is separate from the screening decision; a pending delivery
          does not change the decision.
        </p>
        {events.length === 0 ? (
          <p className="text-sm text-[#777]">No events returned.</p>
        ) : (
          <ol className="relative space-y-4 border-l border-[#e8e8e8] pl-5">
            {events.map((event) => {
              const reason = event.payload['reason'];
              return (
                <li key={event.event_id} className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full bg-[#6B0F1A]"
                  />
                  <p className="text-sm font-semibold text-[#333]">{label(event.label)}</p>
                  <p className="text-xs text-[#777]">
                    {event.actor} · {time(event.occurred_at)}
                  </p>
                  {typeof reason === 'string' && (
                    <p className="mt-1 break-words text-sm text-[#555]">Reason: {reason}</p>
                  )}
                  <p className="mt-1 text-xs">
                    {event.delivery_status === 'delivered' ? (
                      <span className="text-[#2e7d32]">
                        Delivered to clients webapp {time(event.delivered_at)}
                      </span>
                    ) : (
                      <span className="text-[#784600]">
                        Delivery pending · {event.delivery_attempts} attempt
                        {event.delivery_attempts === 1 ? '' : 's'}
                        {event.delivery_last_error ? ' · last error ' : ''}
                        {event.delivery_last_error && <span>{event.delivery_last_error}</span>}
                      </span>
                    )}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </>
  );
}

function Identifier({ name, value }: { name: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1 border-b border-[#f0f0f0] py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <dt className="text-xs text-[#777]">{name}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-2">
        {value ? (
          <>
            <code className="break-all font-mono text-xs text-[#333]">{value}</code>
            <CopyButton value={value} name={name} />
          </>
        ) : (
          <span className="text-xs text-[#999]">Not available</span>
        )}
      </dd>
    </div>
  );
}

export function TechnicalDetails({ row }: { row: ScreeningDetail }) {
  const result = row.result;
  return (
    <>
      <p className="text-sm text-[#666]">
        Identifiers and raw evidence for diagnosis and provider correspondence.
      </p>
      <div>
        <Heading>Identifiers</Heading>
        <dl className="rounded-md border border-[#eee] px-3">
          <Identifier name="Screening ID" value={row.screening_id} />
          <Identifier name="CBS transaction ID" value={row.transaction_id} />
          <Identifier name="Tenant" value={row.tenant_cod} />
          <Identifier name="Client ID" value={row.client_id} />
          <Identifier name="Screening provider" value={row.provider} />
          <Identifier name="Provider transaction reference" value={row.provider_ref} />
          <Identifier
            name="Counterparty AML request reference"
            value={result.counterparty_aml?.request_ref}
          />
          <Identifier
            name="Standalone AML request reference"
            value={result.standalone_aml?.request_ref}
          />
        </dl>
      </div>
      <div>
        <Heading>Dispatch and synchronisation</Heading>
        <Facts
          values={{
            Requested: time(row.requested_at),
            Decided: time(row.decided_at),
            'Decision applied': row.applied_at ? time(row.applied_at) : 'Not applied',
            'Dispatch attempts': row.attempts,
            'Next attempt': row.next_attempt_at
              ? time(row.next_attempt_at)
              : 'No next attempt scheduled',
            'Last error category': row.last_error ?? 'No dispatch error recorded',
            'Last successful sync': time(row.last_synced_at),
          }}
        />
        <p className="mt-3 text-xs text-[#777]">
          A missing sync time means no successful sync is recorded. A successful sync does not
          establish that no later webhook failed.
        </p>
      </div>
      <div>
        <Heading>Rule identifiers</Heading>
        {result.rule_runs.length === 0 ? (
          <p className="text-sm text-[#777]">Not returned.</p>
        ) : (
          <dl className="rounded-md border border-[#eee] px-3">
            {result.rule_runs.map((rule, i) => (
              <Identifier key={`${rule.rule_id}-${i}`} name={rule.title} value={rule.rule_id} />
            ))}
          </dl>
        )}
      </div>
      <div>
        <Heading>Structured evidence</Heading>
        <Structured title="Submitted custom properties" value={result.custom_properties_sent} />
        <Structured title="Provider review activities" value={result.provider_activities ?? []} />
        {result.transaction_evidence && (
          <>
            <Structured
              title="Provider results (not attributed to a party)"
              value={result.transaction_evidence.provider_results}
            />
            <Structured
              title="Parties returned by the provider"
              value={result.transaction_evidence.parties}
            />
          </>
        )}
        {row.hits.length ? (
          row.hits.map((hit) => (
            <Structured
              key={hit.hit_id}
              title={`Persisted finding ${hit.hit_id} (${label(hit.type)}, ${hit.source})`}
              value={hit}
            />
          ))
        ) : (
          <p className="mt-3 text-sm text-[#777]">No persisted hits returned.</p>
        )}
      </div>
      <div>
        <Heading>Event records</Heading>
        {row.events.length === 0 ? (
          <p className="text-sm text-[#777]">No events returned.</p>
        ) : (
          row.events.map((event) => (
            <div key={event.event_id} className="mt-3">
              <dl className="rounded-md border border-[#eee] px-3">
                <Identifier name={`Event ID — ${label(event.label)}`} value={event.event_id} />
              </dl>
              <Structured title={`${label(event.label)} event payload`} value={event.payload} />
              {event.delivery_next_attempt_at && (
                <p className="mt-1 text-xs text-[#777]">
                  Next delivery attempt {time(event.delivery_next_attempt_at)}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
}
